import slugify from 'slugify';
import crypto from 'crypto';

// TODO: Since getSlug can be called with resolved values from labels-service which can contain HTML tags, should we consider stripping out HTML?
// Watch out for JS execution vulnerability, per https://stackoverflow.com/questions/5002111/how-to-strip-html-tags-from-string-in-javascript
/**
 * Converts a string into a valid URL slug
 *
 * Ex. 1: with arg ('Department 1') returns 'department-1'
 * Ex. 2: with arg ('Department 2') returns 'department-2'
 *
 * Uses 'slugify' as a dependency: https://github.com/simov/slugify
 *
 * @param {String} dept
 * @returns {String}
 */
export const getSlug = (dept = '') => {
	const options = { lower: true, strict: true, remove: /_/g };
	const slug = slugify(dept, options);
	return slug;
};

export const generateId = () => {
	const uuid = crypto.randomUUID().substring(2, 8); // base36 for shorter string
	return `ID_${uuid}`;
};

export const getLabelsFromItems = (items = []) =>
	items.reduce((acc, item) => {
		if (item?.label) acc.push(item.label);
		return acc;
	}, []);

const CONTACT_ACTION_ARIA_LABELS = {
	call: {
		id: 'CALL_STAFF_MEMBER_ARIA_LABEL',
		fallback: 'Call {name} at {phone}'
	},
	email: {
		id: 'MESSAGE_STAFF_MEMBER_ARIA_LABEL',
		fallback: 'Message {name}'
	},
	info: {
		id: 'ABOUT_STAFF_MEMBER_ARIA_LABEL',
		fallback: 'About {name}'
	}
};

/**
 * Builds the accessible name for one of a staff card's contact action icons.
 *
 * The icons render with no visible text, so without this an assistive
 * technology announces only the generic icon - no action, no staff member.
 *
 * Ex. 1: with ('call', { name: 'John Smith', phone: '555-555-5555' })
 *        returns 'Call John Smith at 555-555-5555'
 * Ex. 2: with ('info', { name: 'John Smith' }) returns 'About John Smith'
 *
 * @param {Object} labels labels object from `useLabels()`
 * @param {String} iconName one of 'call', 'email', 'info'
 * @param {Object} staffMember the staff member's itemlist record
 * @returns {String} the accessible name, or '' for an unrecognized iconName
 */
export const getContactActionAriaLabel = (labels, iconName, staffMember) => {
	const template = CONTACT_ACTION_ARIA_LABELS[iconName];
	if (!template) return '';
	const resolved = labels?.get?.(template.id);
	const text =
		resolved && resolved !== template.id ? resolved : template.fallback;

	// Collapse the whitespace left behind by any token we could not populate,
	// so a sparse record never produces 'Call undefined at ...' or 'About  '.
	return text
		.replace(/\{name\}/g, staffMember?.name || '')
		.replace(/\{phone\}/g, staffMember?.phone || '')
		.replace(/\s+/g, ' ')
		.trim();
};
